import { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  Button,
} from '@mui/material';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { useTenant } from '../providers/TenantProvider';
import type { Lead, Member, CheckIn } from '../lib/db/types';

interface LeadDoc extends Lead {
  id: string;
}

interface MemberDoc extends Member {
  id: string;
}

interface CheckInDoc extends CheckIn {
  id: string;
}

export const DashboardPage = () => {
  const { user, tenantId, role } = useTenant();
  const [leads, setLeads] = useState<LeadDoc[]>([]);
  const [members, setMembers] = useState<MemberDoc[]>([]);
  const [recentCheckins, setRecentCheckins] = useState<CheckInDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Load leads
        const leadsQuery = query(
          collection(db, 'leads'),
          where('tenantId', '==', tenantId),
          orderBy('createdAt', 'desc'),
          limit(5)
        );
        const leadsSnapshot = await getDocs(leadsQuery);
        const leadsData = leadsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as LeadDoc[];
        setLeads(leadsData);

        // Load members
        const membersQuery = query(
          collection(db, 'members'),
          where('tenantId', '==', tenantId),
          where('status', '==', 'active'),
          orderBy('joinedAt', 'desc'),
          limit(5)
        );
        const membersSnapshot = await getDocs(membersQuery);
        const membersData = membersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as MemberDoc[];
        setMembers(membersData);

        // Load recent check-ins
        const checkinsQuery = query(
          collection(db, 'checkins'),
          where('tenantId', '==', tenantId),
          orderBy('timestamp', 'desc'),
          limit(10)
        );
        const checkinsSnapshot = await getDocs(checkinsQuery);
        const checkinsData = checkinsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as CheckInDoc[];
        setRecentCheckins(checkinsData);

        console.log('Data loaded:', {
          leads: leadsData.length,
          members: membersData.length,
          checkins: checkinsData.length,
        });
      } catch (err: any) {
        console.error('Error loading data:', err);
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [tenantId]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography>Loading dashboard...</Typography>
      </Box>
    );
  }

  // If no tenantId, show setup instructions
  if (!tenantId) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
        <Container maxWidth="md">
          <Paper sx={{ p: 4 }}>
            <Typography variant="h4" gutterBottom color="error">
              Account Setup Required
            </Typography>
            <Typography variant="body1" paragraph>
              Your account does not have tenant access configured yet.
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              <Box component="strong" sx={{ fontWeight: 700 }}>User Email:</Box> {user?.email}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              <Box component="strong" sx={{ fontWeight: 700 }}>User ID:</Box> {user?.uid}
            </Typography>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" gutterBottom>
              Quick Setup
            </Typography>
            <Typography variant="body2" paragraph>
              Go to the setup page to configure your account:
            </Typography>
            <Button
              variant="contained"
              color="primary"
              href="/setup"
              sx={{ mb: 3 }}
            >
              Go to Setup Page
            </Button>
            <Typography variant="caption" display="block" color="text.secondary">
              You'll need to set your tenant ID (use "tenant-demo-gym" for testing) and role.
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
      <Container maxWidth="lg">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Welcome back, {user?.email} ({role})
          </Typography>
        </Box>

        {error && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'error.light', color: 'error.contrastText' }}>
            <Typography>{error}</Typography>
          </Paper>
        )}

        <Grid container spacing={3}>
          {/* Stats Cards */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Active Members
                </Typography>
                <Typography variant="h3">{members.length}</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  New Leads
                </Typography>
                <Typography variant="h3">{leads.length}</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Today's Check-ins
                </Typography>
                <Typography variant="h3">{recentCheckins.length}</Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Recent Leads */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Recent Leads
              </Typography>
              <List>
                {leads.map((lead) => {
                  // Handle both flat and nested contact structure
                  const leadAny = lead as any;
                  const contact = lead.contact || {
                    firstName: leadAny.firstName,
                    lastName: leadAny.lastName,
                    email: leadAny.email,
                    phone: leadAny.phone,
                  };
                  
                  return (
                    <ListItem key={lead.id} divider>
                      <ListItemText
                        primary={`${contact.firstName} ${contact.lastName}`}
                        secondary={
                          <>
                            {contact.email} • {contact.phone || 'N/A'}
                            <Box component="span" sx={{ display: 'block' }}>
                              Source: {lead.source || 'N/A'}
                            </Box>
                          </>
                        }
                      />
                      <Chip label={lead.pipelineStage} size="small" />
                    </ListItem>
                  );
                })}
                {leads.length === 0 && (
                  <ListItem>
                    <ListItemText secondary="No leads found" />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Grid>

          {/* Active Members */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Active Members
              </Typography>
              <List>
                {members.map((member) => (
                  <ListItem key={member.id} divider>
                    <ListItemText
                      primary={`${member.contact.firstName} ${member.contact.lastName}`}
                      secondary={
                        <>
                          {member.contact.email}
                          <Box component="span" sx={{ display: 'block' }}>
                            Check-ins: {member.attendanceCount || 0}
                          </Box>
                        </>
                      }
                    />
                    <Chip label={member.status} color="success" size="small" />
                  </ListItem>
                ))}
                {members.length === 0 && (
                  <ListItem>
                    <ListItemText secondary="No members found" />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Grid>

          {/* Recent Check-ins */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Recent Check-ins
              </Typography>
              <List>
                {recentCheckins.map((checkin) => (
                  <ListItem key={checkin.id} divider>
                    <ListItemText
                      primary={`Member: ${checkin.memberId}`}
                      secondary={`${new Date(
                        checkin.timestamp.seconds * 1000
                      ).toLocaleString()} • Method: ${checkin.method}`}
                    />
                  </ListItem>
                ))}
                {recentCheckins.length === 0 && (
                  <ListItem>
                    <ListItemText secondary="No check-ins found" />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Grid>
        </Grid>

        <Box sx={{ mt: 4, p: 3, bgcolor: 'info.light', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            Tenant Information
          </Typography>
          <Typography variant="body2" component="div">
            <Box>
              <Box component="strong" sx={{ fontWeight: 700 }}>Tenant ID:</Box> {tenantId}
            </Box>
            <Box>
              <Box component="strong" sx={{ fontWeight: 700 }}>Role:</Box> {role}
            </Box>
            <Box>
              <Box component="strong" sx={{ fontWeight: 700 }}>User ID:</Box> {user?.uid}
            </Box>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};
